import { useState, useEffect } from "react";
import { Flex, Box, Text } from "@chakra-ui/react";
import moment from "moment";

import { ManutencaoDto } from "features/manutencao/typings";
import { useListarManutencoes } from "features/manutencao/hooks/useListarManutencoes";

import PageHeader from "components/Page/PageHeader";
import DateRangePicker from "components/Input/DateRangePicker";

import DashboardManutencao from "./DashboardManutencao";
import ListagemManutencaoTable from "./ListagemManutencaoTable";
import ModalNovoPlanoManutencao from "./ModalNovoPlanoManutencao";

import CalendarioManutencao from "./CalendarioManutencao";
import ModalRealizarManutencao from "./ModalRealizarManutencao";
import ModalHistoricoManutencao from "./ModalHistoricoManutencao";
import useFiltroManutencao from "./ListagemManutencaoTable/hooks/useFiltroManutencao";
import { useRegrasTenant } from "core/features/tenants/tenants.context";
import ModalInicioSubtenantsView from "components/ModalInicioSubtenantsView";
import ModalFiltros from "./ModalFiltros";
import { useLocation } from "react-router";

export default function ListaManutencao() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const manutencaoId = params.get("manutencaoId") as string;

  const { useSubtenantsViews } = useRegrasTenant();

  const [view, setView] = useState<"CALENDARIO" | "LISTA">("LISTA");
  const [isOpenModal, setIsOpenModal] = useState(!!manutencaoId);
  const [activeListQuery, setActiveListQuery] = useState<boolean>(
    !useSubtenantsViews
  );

  const { filtro, onChangeFiltro, onResetFiltro } = useFiltroManutencao();

  const { data, isLoading, isFetching, refetch } = useListarManutencoes(
    {
      ...filtro,
      limit: view === "CALENDARIO" ? 9999 : 10,
      page: view === "CALENDARIO" ? 1 : filtro.page,
    },
    activeListQuery
  );

  const [isOpenModalInicioSubtenantsView, setIsOpenModalInicioSubtenantsView] =
    useState(useSubtenantsViews);
  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);

  const [idManutencaoHistorico, setIdManutencaoHistorico] = useState<
    string | undefined
  >();
  const [idManutencaoEditar, setIdManutencaoEditar] = useState<
    string | undefined
  >(manutencaoId);

  const [manutencaoDto, setManutencaoDto] = useState<
    ManutencaoDto | undefined
  >();

  useEffect(() => {
    if (idManutencaoEditar) {
      setIsOpenModal(true);
    }
  }, [idManutencaoEditar]);

  useEffect(() => {
    if (!isOpenModal) {
      setIdManutencaoEditar(undefined);
    }
  }, [isOpenModal]);

  const numFiltros =
    (filtro.tenantsIds?.length ? 1 : 0) +
    (filtro.dataInicio || filtro.dataFim ? 1 : 0);

  return (
    <>
      <PageHeader
        title="Controle de Manutenção"
        mb="24px"
        titleButton={
          !useSubtenantsViews ? "Novo Plano de Manutenção" : undefined
        }
        onClick={!useSubtenantsViews ? () => setIsOpenModal(true) : undefined}
        extraButtonTitle={useSubtenantsViews ? "Filtros avançados" : undefined}
        extraButtonOnClick={
          useSubtenantsViews ? () => setIsOpenModalFiltro(true) : undefined
        }
        badge
        badgeValueExtraButton={numFiltros}
        midComponent={
          <Flex>
            <Box w="250px">
              <DateRangePicker
                allowEmptyDates={{
                  dataInicio: false,
                  dataFim: false,
                }}
                onChange={(dataInicio, dataFim) => {
                  onChangeFiltro({
                    dataInicio,
                    dataFim,
                  });
                }}
                value={{
                  dataInicio: filtro.dataInicio,
                  dataFim: filtro.dataFim,
                }}
                disabled={view === "CALENDARIO"}
              />
            </Box>
          </Flex>
        }
      />
      <DashboardManutencao
        filtro={filtro}
        onFiltroStatus={({ statusId, emAtraso }) => {
          onChangeFiltro({
            statusId: statusId === filtro?.statusId ? undefined : statusId,
            emAtraso,
          });
        }}
      />

      {!activeListQuery ? (
        <Box my="32px">
          <Text textAlign="center" fontSize="16px" fontWeight="medium">
            Utilize os filtros avançados para carregar a lista de manutenções
          </Text>
        </Box>
      ) : null}

      {view === "LISTA" ? (
        <ListagemManutencaoTable
          data={data}
          filtro={filtro}
          isLoading={isLoading || isFetching}
          onChangeFiltro={onChangeFiltro}
          setIdManutencaoEditar={setIdManutencaoEditar}
          setManutencaoDto={setManutencaoDto}
          setIdManutencaoHistorico={setIdManutencaoHistorico}
          onChangeView={() => setView("CALENDARIO")}
          refetch={refetch}
        />
      ) : (
        <CalendarioManutencao
          manutencoes={data?.items}
          onChangeDate={(start, end) => {
            onChangeFiltro({
              dataInicio: moment(start).format("YYYY-MM-DD"),
              dataFim: moment(end).format("YYYY-MM-DD"),
            });
          }}
          onClick={setManutencaoDto}
          onChangeView={() => setView("LISTA")}
          filtro={filtro}
          onChangeFiltro={onChangeFiltro}
          loading={isLoading || isFetching}
          hideOptionsView
        />
      )}

      <ModalRealizarManutencao
        onClose={() => setManutencaoDto(undefined)}
        visible={!!manutencaoDto?.id}
        manutencaoDto={manutencaoDto}
        refetchList={refetch}
      />

      <ModalNovoPlanoManutencao
        id={idManutencaoEditar}
        refetchList={refetch}
        onClose={() => setIsOpenModal(false)}
        visible={isOpenModal}
      />

      <ModalHistoricoManutencao
        onClose={() => setIdManutencaoHistorico(undefined)}
        id={idManutencaoHistorico}
        isOpenModal={!!idManutencaoHistorico}
      />

      <ModalFiltros
        isOpen={isOpenModalFiltro}
        onClose={() => setIsOpenModalFiltro(false)}
        filtro={filtro}
        onChangeFiltro={(filtro) => {
          onChangeFiltro(filtro);
          setIsOpenModalFiltro(false);
          setActiveListQuery(true);
        }}
        onReset={() => {
          onResetFiltro();
          setIsOpenModalFiltro(false);
          setActiveListQuery(true);
        }}
      />

      {useSubtenantsViews && isOpenModalInicioSubtenantsView ? (
        <ModalInicioSubtenantsView
          isOpen={isOpenModalInicioSubtenantsView}
          onClose={() => setIsOpenModalInicioSubtenantsView(false)}
          onOpenFiltros={() => {
            setIsOpenModalFiltro(true);
            setIsOpenModalInicioSubtenantsView(false);
          }}
          onVerTudo={() => {
            setActiveListQuery(true);
            setIsOpenModalInicioSubtenantsView(false);
          }}
        />
      ) : null}
    </>
  );
}
